import { TFunction } from 'i18next';
import moment from 'moment';

import {
  CareerMemberLearningProgressQuery,
  ErpCareerDashboardQuery,
  ErpCareerExerciseTemplatesQuery,
  ErpCareerExerciseVocabularyQuery,
  ErpCareerLessonsQuery,
  ErpCareerMemberDetailQuery,
  ErpCareerMembersQuery,
  ErpCareerPromoCampaignsQuery,
  ErpCareerPromoCodesQuery,
  ErpCareerSkillsQuery,
  ErpVacanciesQuery,
  Maybe,
} from 'core/graphql';

/**
 * Mirror of the shared `TagTone` union (kept local so `core` never imports up
 * into `components/**` and breaks the Atomic Design import direction). The
 * vacancy status→tone map below returns one of these, and the Вакансии table
 * feeds it straight into the shared `<Tag tone>`.
 */
export type VacancyTagTone =
  | 'ok'
  | 'warn'
  | 'danger'
  | 'neutral'
  | 'info'
  | 'gold';

/**
 * Spec 0018 (career center), increment A — the model layer.
 *
 * Wire enums are plain Strings; every narrowing of a `status` /
 * `completionKind` string happens here so components never compare raw
 * literals and the i18n key sets stay closed.
 */

/** One row of the Участники table. */
export type ICareerMember = NonNullable<
  ErpCareerMembersQuery['careerMembers'][number]
>;

/** One row of the Обучение table. */
export type ICareerLesson = NonNullable<
  ErpCareerLessonsQuery['careerLessonsErp'][number]
>;

/**
 * One uploaded lesson file (spec 0020). Derived from the query so it stays in
 * lock-step with the wire shape; the video/attachment mutations select the same
 * fields, so their returned rows are structurally interchangeable with this.
 */
export type ICareerLessonAttachment = ICareerLesson['attachments'][number];

/**
 * Spec 0020 Фаза B — the staff completion funnel for one lesson. Nullable on the
 * wire (the backend serves `stats` only in ERP scope), so the funnel columns
 * fall back to «—» when it is absent.
 */
export type ICareerLessonStats = NonNullable<ICareerLesson['stats']>;

/**
 * Spec 0020 Фаза C — one quiz question as the ERP author sees it. Unlike the LMS
 * scope, `correctIndexes` is populated here, so the editor can pre-check the
 * right options. Derived from the query so it stays in lock-step with the shape
 * `SaveCareerLessonQuiz` echoes back.
 */
export type ICareerQuizQuestion = ICareerLesson['quizQuestions'][number];

/**
 * Spec 0020 Фаза B — one lesson row of a member's learning map (the progress
 * drawer, `careerMemberProgress`). `openedAt`/`completedAt` are the lone
 * nullable fields.
 */
export type ICareerMemberLessonProgress = NonNullable<
  CareerMemberLearningProgressQuery['careerMemberProgress'][number]
>;

/** Where a lesson sits in one member's journey: done, started, or untouched. */
export type CareerLessonProgressState = 'completed' | 'opened' | 'notStarted';

export const careerLessonProgressState = (
  row: Pick<ICareerMemberLessonProgress, 'isOpened' | 'isCompleted'>,
): CareerLessonProgressState => {
  if (row.isCompleted) return 'completed';
  if (row.isOpened) return 'opened';
  return 'notStarted';
};

/**
 * Localized label for a progress state. Built with a `${state}` suffix (never a
 * bare literal) so the locale smoke-test scanner treats it as dynamic — the
 * union is closed, so parity across ru/pl is what guarantees the key resolves.
 */
export const careerLessonProgressStateLabel = (
  t: TFunction,
  state: CareerLessonProgressState,
): string => t(`pages.careerCenter.members.progress.state.${state}`);

/** Progress state → shared `<Tag>` tone for the drawer's status pill. */
export const careerLessonProgressStateTone = (
  state: CareerLessonProgressState,
): VacancyTagTone => {
  switch (state) {
    case 'completed':
      return 'ok';
    case 'opened':
      return 'info';
    default:
      return 'neutral';
  }
};

/**
 * The mutable media half of a lesson the form owns locally: the video URL plus
 * the current attachment list. Every media mutation (`uploadVideo`,
 * `clearVideo`, `addAttachment`) returns one of these so the form can replace
 * its local media state wholesale; `removeAttachment` is the lone exception —
 * it returns only `ok`, so the form drops the row by id itself.
 */
export interface ICareerLessonMedia {
  videoUrl?: string | null;
  attachments: ICareerLessonAttachment[];
}

/** The full member card behind the Участники detail (increment B1). */
export type ICareerMemberDetail = NonNullable<
  ErpCareerMemberDetailQuery['careerMemberDetail']
>;

/**
 * The structured graduate profile. `careerMemberDetail.profile` is nullable —
 * a member who has not started their profile in the LMS reads back `null`, and
 * ERP renders the muted «профиль не заполнен» state for that.
 */
export type ICareerProfile = NonNullable<ICareerMemberDetail['profile']>;

export type ICareerProfileSkill = ICareerProfile['skills'][number];
export type ICareerProfileProject = ICareerProfile['projects'][number];
export type ICareerProfileExperience = ICareerProfile['experience'][number];
export type ICareerProfileEducation = ICareerProfile['education'][number];
export type ICareerProfileLanguage = ICareerProfile['languages'][number];
export type ICareerProfileCertificate = ICareerProfile['certificates'][number];

/**
 * Skill categories the backend tags a `Skill` with (spec §3.1). Sent on the
 * wire as a plain String; casing is normalised in `careerSkillCategoryLabel`
 * so grouping/labelling survives an uppercase or lowercase server value.
 */
export const CAREER_SKILL_CATEGORIES = [
  'language',
  'framework',
  'library',
  'tool',
  'database',
  'cloud',
  // ТЗ 0039 доводка: категория была в бэкенде с самого начала, а в
  // этом списке её не было — восемь навыков («Machine Learning», «OWASP
  // Top 10», «сети», «криптография» и другие) не имели ни ярлыка, ни
  // варианта в форме, то есть перевести навык в неё из ERP было нельзя.
  'domain',
  'soft',
] as const;

export type CareerSkillCategory = typeof CAREER_SKILL_CATEGORIES[number];

const isCareerSkillCategory = (value: string): value is CareerSkillCategory =>
  (CAREER_SKILL_CATEGORIES as readonly string[]).includes(value);

/**
 * Localized label for a skill category. Normalises casing first (backend may
 * spell it `FRAMEWORK` or `framework`); an unknown category degrades to the
 * raw server string rather than an i18n key path.
 */
export const careerSkillCategoryLabel = (
  t: TFunction,
  category: string,
): string => {
  const normalized = (category || '').toLowerCase();
  return isCareerSkillCategory(normalized)
    ? t(`pages.careerCenter.profile.skillCategory.${normalized}`)
    : category || t('pages.careerCenter.profile.skillCategory.other');
};

/** Stable sort order for the grouped skill sections. */
export const careerSkillCategoryOrder = (category: string): number => {
  const normalized = (category || '').toLowerCase();
  const index = (CAREER_SKILL_CATEGORIES as readonly string[]).indexOf(
    normalized,
  );
  return index === -1 ? CAREER_SKILL_CATEGORIES.length : index;
};

export interface ICareerCustomError {
  field?: Maybe<string>;
  message?: Maybe<string>;
  code?: Maybe<string>;
}

/** Member lifecycle, exactly as the backend spells it on the wire. */
export const CAREER_MEMBER_STATUSES = [
  'active',
  'paused',
  'placed',
  'archived',
  // ТЗ 0039 доводка. «Убран» ≠ «В архиве»: архив — человек ПРОШЁЛ свой путь,
  // убран — его тут быть не должно было. Один статус на оба случая заставил бы
  // воронку считать выпускника и опечатку оператора одинаково.
  'removed',
] as const;

export type CareerMemberStatus = typeof CAREER_MEMBER_STATUSES[number];

export const isCareerMemberStatus = (
  value: string,
): value is CareerMemberStatus =>
  (CAREER_MEMBER_STATUSES as readonly string[]).includes(value);

/**
 * How a lesson is marked done: self-acknowledge, hand in an artifact link, or
 * — since ТЗ 0039 — fill in the lesson's exercise.
 *
 * 🔴 Список обязан содержать ВСЕ виды, которые умеет бэкенд. Забытый вид не
 * ломается громко: `careerCompletionKindLabel` отдаёт сырую строку, и в
 * колонке способа появляется английское `exercise`, а в форме урока способ
 * вообще нельзя выбрать — то есть анкету некуда включить.
 */
export const CAREER_COMPLETION_KINDS = [
  'acknowledge',
  'artifact',
  'exercise',
] as const;

export type CareerCompletionKind = typeof CAREER_COMPLETION_KINDS[number];

export const isCareerCompletionKind = (
  value: string,
): value is CareerCompletionKind =>
  (CAREER_COMPLETION_KINDS as readonly string[]).includes(value);

/**
 * Localized label for a wire status. An unknown value (backend grew a status
 * the frontend has not shipped a key for yet) degrades to the raw string
 * rather than rendering an i18n key path to the operator.
 */
export const careerStatusLabel = (t: TFunction, status: string): string =>
  isCareerMemberStatus(status)
    ? t(`pages.careerCenter.status.${status}`)
    : status;

/**
 * Тот же статус во множественном числе — для плиток сводки и полос фильтра.
 *
 * 🔴 Не «мелочь» и не вкусовщина. Подпись строки описывает ОДНОГО человека
 * («Активен»), а плитка — их множество, и «3 · Активен» читается как обрывок
 * фразы. Первый заход переиспользовал строчную подпись, и на стенде это видно
 * сразу.
 */
export const careerStatusPluralLabel = (t: TFunction, status: string): string =>
  isCareerMemberStatus(status)
    ? t(`pages.careerCenter.statusPlural.${status}`)
    : status;

/**
 * Статус участника → тон плашки.
 *
 * 🔴 До этого все пять статусов рисовались литералом `tone="neutral"`:
 * «Трудоустроен» — исход, ради которого раздел и существует, — выглядел ровно
 * как «Убран» и как «Активен». Оператор не различал их ни в таблице, ни в
 * фильтре.
 *
 * У соседних сущностей сопоставитель есть везде (`vacancyStatusTone`,
 * `careerLessonProgressStateTone`); у участников его просто не завели.
 *
 * `removed` красный, а `archived` серый намеренно: архив — нормальное
 * завершение работы с человеком, «убран» — отзыв доступа.
 */
export const careerMemberStatusTone = (status: string): VacancyTagTone => {
  switch (status) {
    case 'placed':
      return 'ok';
    case 'active':
      return 'info';
    case 'paused':
      return 'warn';
    case 'removed':
      return 'danger';
    default:
      return 'neutral';
  }
};

export const careerCompletionKindLabel = (t: TFunction, kind: string): string =>
  isCareerCompletionKind(kind)
    ? t(`pages.careerCenter.completionKind.${kind}`)
    : kind;

/** `3 / 8` — the learning-progress cell. */
export const careerProgressLabel = (completed: number, total: number): string =>
  `${completed} / ${total}`;

/* ── Глубина владения навыком (ТЗ 0040, доводка) ─────────────────────── */

/**
 * Полоса шкалы 1–5, в которую попадает уровень.
 *
 * 🔴 Границы — зеркало `career/services/matching.py:147-153`, единственного
 * места, где система сама трактует эту шкалу: по этим же порогам считается
 * подбор вакансий. Разъедутся — подпись на экране начнёт спорить с тем, как
 * система ранжирует, и спорить молча.
 */
export const careerSkillDepthBand = (
  level: number,
): 'basic' | 'working' | 'confident' => {
  if (level >= 4) return 'confident';
  if (level === 3) return 'working';
  return 'basic';
};

/** Слово полосы: «по верхам» / «рабочий уровень» / «уверенно». */
export const careerSkillDepthLabel = (t: TFunction, level: number): string =>
  t(`pages.careerCenter.skills.depth.${careerSkillDepthBand(level)}`);

/**
 * Подпись к точкам: «Уровень 4 из 5 · уверенно».
 *
 * Собирается ЗДЕСЬ, а не в атоме `LevelDots`: локаль-гейт
 * `careerCenter.locale.test.ts` сканирует этот файл и не сканирует `atoms/`.
 * Ключ, вызванный из атома, выпал бы из проверки паритета ru/pl незаметно.
 *
 * Существительное «Уровень» тут допустимо только внутри подсказки: на самих
 * экранах ERP колонка называется «Глубина», потому что «Уровень» уже занят
 * очками опыта (`pages.careerCenter.growth.levels.*` — Новичок…Профессионал),
 * и два разных «Уровня 3» на соседних экранах путали бы.
 */
export const careerSkillDepthTitle = (t: TFunction, level: number): string =>
  t('pages.careerCenter.skills.depthTooltip', {
    n: level,
    band: careerSkillDepthLabel(t, level),
  });

/* ── Increment B — vacancies + dashboard ─────────────────────────────── */

/** One row of the Вакансии table. */
export type IVacancy = NonNullable<
  ErpVacanciesQuery['careerVacanciesErp'][number]
>;

/** The funnel counters behind the Дашборд tab. */
export type ICareerDashboard = ErpCareerDashboardQuery['careerDashboard'];

/** One skill option for the vacancy-form multiselect. */
export type ICareerSkill = NonNullable<
  ErpCareerSkillsQuery['careerSkillsErp'][number]
>;

/**
 * Vacancy lifecycle, exactly as the backend spells it on the wire. Phase 1
 * staff walk DRAFT→PUBLISHED by hand; APPROVED/REJECTED are the moderation
 * queue reserved for phase 2 but already settable here.
 */
export const VACANCY_STATUSES = [
  'draft',
  'approved',
  'published',
  'rejected',
  'archived',
] as const;

export type VacancyStatus = typeof VACANCY_STATUSES[number];

export const isVacancyStatus = (value: string): value is VacancyStatus =>
  (VACANCY_STATUSES as readonly string[]).includes(value);

/**
 * Localized label for a wire status; an unknown value degrades to the raw
 * string rather than an i18n key path (same rule as `careerStatusLabel`).
 */
export const vacancyStatusLabel = (t: TFunction, status: string): string =>
  isVacancyStatus(status)
    ? t(`pages.careerCenter.vacancies.status.${status}`)
    : status;

/** Status → shared `<Tag>` tone. */
export const vacancyStatusTone = (status: string): VacancyTagTone => {
  switch (status) {
    case 'published':
      return 'ok';
    case 'approved':
      return 'info';
    case 'rejected':
      return 'danger';
    case 'archived':
      return 'warn';
    default:
      return 'neutral';
  }
};

/** Work-mode / seniority / employment-type reference lists (wire Strings). */
export const VACANCY_WORK_MODES = ['onsite', 'hybrid', 'remote'] as const;
export const VACANCY_SENIORITIES = [
  'intern',
  'junior',
  'middle',
  'senior',
] as const;
export const VACANCY_EMPLOYMENT_TYPES = [
  'full_time',
  'part_time',
  'internship',
  'contract',
] as const;

export type VacancyWorkMode = typeof VACANCY_WORK_MODES[number];
export type VacancySeniority = typeof VACANCY_SENIORITIES[number];
export type VacancyEmploymentType = typeof VACANCY_EMPLOYMENT_TYPES[number];

/**
 * Localized labels for the three vacancy enums. Each builds its i18n key with a
 * `${value}` suffix (never a bare `pages.careerCenter.*.workMode` literal) so an
 * unknown wire value degrades to the raw string — and so the locale smoke test's
 * static-key scanner treats these as dynamic instead of demanding an object node
 * resolve to a string leaf.
 */
export const vacancyWorkModeLabel = (t: TFunction, value: string): string =>
  value && (VACANCY_WORK_MODES as readonly string[]).includes(value)
    ? t(`pages.careerCenter.vacancies.workMode.${value}`)
    : value || '—';

/**
 * Spec 0034 — where the row came from. Stamped by the backend off the request
 * (an ERP session vs an MCP one), never sent by the client, so a moderator can
 * tell an AI agent's draft from a colleague's.
 */
export const VACANCY_SOURCES = ['erp', 'mcp'] as const;

export type VacancySource = typeof VACANCY_SOURCES[number];

export const vacancySourceLabel = (t: TFunction, value: string): string =>
  (VACANCY_SOURCES as readonly string[]).includes(value)
    ? t(`pages.careerCenter.vacancies.source.${value}`)
    : value;

/** Agent-created rows get the accent tone — that is the whole point of it. */
export const vacancySourceTone = (value: string): VacancyTagTone =>
  value === 'mcp' ? 'gold' : 'neutral';

/** Freshness filter values; `''` means "no filter". */
export const VACANCY_FRESHNESS = ['', 'fresh', 'expired'] as const;

export const vacancyFreshnessLabel = (t: TFunction, value: string): string =>
  t(`pages.careerCenter.vacancies.freshness.${value || 'all'}`);

/**
 * Past its `expiresAt`. An empty date means open-ended, NOT expired — the same
 * rule the backend's `expiry_q` applies to the student feed.
 */
export const vacancyIsExpired = (
  vacancy: Pick<IVacancy, 'expiresAt'>,
): boolean => {
  if (!vacancy.expiresAt) return false;
  return moment(vacancy.expiresAt).isBefore(moment().startOf('day'));
};

export const vacancySeniorityLabel = (t: TFunction, value: string): string =>
  value && (VACANCY_SENIORITIES as readonly string[]).includes(value)
    ? t(`pages.careerCenter.vacancies.seniority.${value}`)
    : value || '—';

export const vacancyEmploymentTypeLabel = (
  t: TFunction,
  value: string,
): string =>
  value && (VACANCY_EMPLOYMENT_TYPES as readonly string[]).includes(value)
    ? t(`pages.careerCenter.vacancies.employmentType.${value}`)
    : value || '—';

/** `12 000–18 000 PLN` / `от 12 000 PLN` / `—` — the salary cell. */
export const vacancySalaryLabel = (
  vacancy: Pick<IVacancy, 'salaryMin' | 'salaryMax' | 'salaryCurrency'>,
): string => {
  const { salaryMin, salaryMax, salaryCurrency } = vacancy;
  const currency = salaryCurrency || '';
  const fmt = (n: number) => n.toLocaleString('ru-RU');
  if (salaryMin != null && salaryMax != null) {
    return `${fmt(salaryMin)}–${fmt(salaryMax)} ${currency}`.trim();
  }
  if (salaryMin != null) return `${fmt(salaryMin)} ${currency}`.trim();
  if (salaryMax != null) return `${fmt(salaryMax)} ${currency}`.trim();
  return '—';
};

/**
 * Maps the backend `customErrors` envelope to a display string, mirroring
 * `surveyErrorMessage`: a known code becomes a localized sentence, anything
 * else falls back to the server text, then to a generic message.
 */
export const careerErrorMessage = (
  t: TFunction,
  customErrors?: Maybe<Array<Maybe<ICareerCustomError>>>,
): string => {
  const first = (customErrors || []).find(Boolean);
  const code = first?.code || '';
  if (code) {
    const translated = t(`pages.careerCenter.errors.${code}`, {
      defaultValue: '',
    });
    if (translated) return translated;
  }
  return first?.message || t('pages.careerCenter.errors.generic');
};

// ── ТЗ 0039 — справочник навыков и навыки курса ──────────────────────

/** Строка справочника во вкладке «Навыки». */
export interface ICareerSkillDictionaryRow {
  id: string;
  name: string;
  slug: string;
  category: string;
  description: string;
  aliases: string[];
  /**
   * Картинка, загруженная оператором. `null` — НЕ «иконки нет»: фронт возьмёт
   * встроенный знак по слагу, а если знака нет — нарисует буквенную плашку.
   */
  iconUrl: string | null;
  isActive: boolean;
  /** Сколько резюме держат навык (скрытые не считаются). */
  holderCount: number;
  /** Сколько курсов его дают. */
  courseCount: number;
  /**
   * Живых вакансий с этим навыком — из НОЧНОГО СНИМКА (ТЗ 0039 шаг 5), того
   * же, что видит студент на карте навыков. `null` — снимок ещё не считали.
   */
  demandVacancies: number | null;
}

/** Строка «курс даёт навык на уровне». */
export interface ICourseSkillRow {
  id: string;
  /** Курс связи. Нужен обратному взгляду «в каких курсах этот навык». */
  courseId: string;
  courseName: string;
  skillId: string;
  skillName: string;
  skillCategory: string;
  level: number;
  /**
   * ТЗ 0040. Сквозной номер занятия, на котором навык открывается студенту.
   * `null` — навык приходит только по завершении всего курса.
   */
  unlockLessonNumber: number | null;
  isActive: boolean;
}

/** ТЗ 0040 — занятие программы курса со сквозным номером. */
export interface ICourseProgramLesson {
  number: number;
  name: string;
  moduleName: string;
  /**
   * 🔴 Идентификаторы обязательны редактору программы: занятие, отправленное
   * обратно БЕЗ `id`, сервер считает новым, а старое на его месте удаляет
   * вместе с материалами.
   */
  lessonId: string;
  moduleId: string;
  /** Второе дерево, в котором висит занятие: им курс показан на сайте. */
  marketingModuleId?: string | null;
  lmsDescription: string;
  taskTypes: string[];
}

/**
 * ТЗ 0041 — строка набора навыков ГРУППЫ.
 *
 * Заменила `ISkillUnlockGroup` («во что номер попадёт в каждой группе курса»):
 * набор снимается с курса при создании группы и дальше принадлежит ей, так что
 * вопрос про соседние группы больше не задаётся.
 */
export interface IGroupSkillRow {
  id: string;
  skillId: string;
  skillName: string;
  skillCategory: string;
  level: number;
  /** `null` — навык придёт по завершении курса. */
  unlockLessonId: string | null;
  unlockLessonNumber: number | null;
  unlockLessonName: string | null;
  unlockLessonDate: string | null;
  order: number;
  isActive: boolean;
}

/** ТЗ 0041 — занятие ГРУППЫ для выбора «навык появляется здесь». */
export interface IGroupProgramLesson {
  lessonId: string;
  number: number;
  name: string;
  moduleName: string;
  date: string | null;
  isOver: boolean;
}

/** Строка списка курсов: сколько навыков и в какие направления входит. */
export interface ICourseSkillSummary {
  courseId: string;
  courseName: string;
  skillCount: number;
  directionTitles: string[];
}

/** Общий результат мутаций справочника. */
export interface ISkillMutationResult {
  success: boolean;
  moved?: number;
  errors: { field: string; message: string }[];
}

/** Разбор «прошёл / не засчитано» по одному курсу (карточка участника). */
export interface ICourseCompletionRow {
  courseId: string;
  courseName: string;
  groupId?: string | null;
  groupName: string;
  completed: boolean;
  derived: boolean;
  hasDiploma: boolean;
  manualStatus: string;
}

/** Один тип поля анкеты из серверного словаря (ТЗ 0039). */
export type IExerciseFieldKind = NonNullable<
  ErpCareerExerciseVocabularyQuery['careerExerciseVocabulary']
>['fieldTypes'][number];

/** Одна цель записи в резюме и типы полей, которые она принимает. */
export type IExerciseBinding = NonNullable<
  ErpCareerExerciseVocabularyQuery['careerExerciseVocabulary']
>['bindings'][number];

/** Анкета урока со стороны конструктора. */
export type ICareerLessonExercise = NonNullable<ICareerLesson['exercise']>;

/** Черновик поля анкеты в конструкторе. */
export interface IExerciseFieldDraft {
  id?: string;
  label: string;
  helpText?: string;
  placeholder?: string;
  fieldType: string;
  required: boolean;
  options: string[];
  binding: string;
  bindingLanguage?: string;
}

/**
 * Всё, что нужно предпросмотру: набор полей. Структурный тип, потому что
 * анкета приезжает двумя разными узлами схемы — прикреплением к уроку и
 * строкой справочника (ТЗ 0040 фаза B).
 */
export interface ICareerExerciseFields {
  fields: ICareerLessonExercise['fields'];
}

/** Анкета в справочнике (ТЗ 0040 фаза B). */
export type ICareerExerciseTemplate = ErpCareerExerciseTemplatesQuery['careerExerciseTemplates'][number];

/** Акция в списке ERP (ТЗ 0039 шаг 6). */
export type IPromoCampaign = NonNullable<
  ErpCareerPromoCampaignsQuery['careerPromoCampaigns'][number]
>;

/** Строка выданного кода. */
export type IPromoCodeRow = NonNullable<
  ErpCareerPromoCodesQuery['careerPromoCodes'][number]
>;
